import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import type { SupportReport as SupportReportData } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useStore } from '@/shared/store/store-context';

import { AttributionCard } from '../components/attribution-card';
import { MissedHeatmap } from '../components/missed-heatmap';
import { RangePicker } from '../components/range-picker';
import { RollupBar } from '../components/rollup-bar';
import { SupportChart } from '../components/support-chart';
import { SupportExportMenu } from '../components/support-export-menu';
import { SupportKpis } from '../components/support-kpis';
import { useDayFormat } from '../hooks/use-day-format';
import { useDurationFormat } from '../hooks/use-duration-format';
import { useReportRange } from '../hooks/use-report-range';
import { useSupportReport } from '../hooks/use-support-report';
import { previousRange } from '../schemas/report-range';

function TableCard({
  title,
  columns,
  isLoading,
  empty,
  children,
}: {
  title: string;
  columns: number;
  isLoading: boolean;
  empty: string | null;
  children: ReactNode;
}) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="border-b px-5 py-4">
        <CardTitle className="text-[15px]">{title}</CardTitle>
      </CardHeader>
      <Table aria-label={title}>
        {children}
        {isLoading ? (
          <TableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={columns} className="px-5">
                  <Skeleton className="h-5" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        ) : empty ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={columns} className="text-muted-foreground px-5 py-6 text-center">
                {empty}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : null}
      </Table>
    </Card>
  );
}

function AgentsTable({
  report,
  isLoading,
}: {
  report: SupportReportData | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const duration = useDurationFormat();
  const agents = report?.agents ?? [];
  return (
    <TableCard
      title={t('support.agents.title')}
      columns={5}
      isLoading={isLoading}
      empty={!isLoading && agents.length === 0 ? t('support.agents.empty') : null}
    >
      <TableHeader>
        <TableRow>
          <TableHead className="pl-5">{t('support.columns.agent')}</TableHead>
          <TableHead className="text-right">{t('support.columns.handled')}</TableHead>
          <TableHead className="text-right">{t('support.columns.firstResponse')}</TableHead>
          <TableHead className="text-right">{t('support.columns.ratings')}</TableHead>
          <TableHead className="pr-5 text-right">{t('support.columns.attributed')}</TableHead>
        </TableRow>
      </TableHeader>
      {!isLoading && agents.length > 0 ? (
        <TableBody>
          {agents.map((agent) => (
            <TableRow key={agent.userId}>
              <TableCell className="pl-5 font-medium">{agent.name}</TableCell>
              <TableCell className="text-right tabular-nums">
                {format.number(agent.handled)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {duration(agent.firstResponseSecondsAvg)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {t('support.agents.ratings', {
                  positive: format.number(agent.ratingsPositive),
                  neutral: format.number(agent.ratingsNeutral),
                  negative: format.number(agent.ratingsNegative),
                })}
              </TableCell>
              <TableCell className="pr-5 text-right tabular-nums">
                {format.money(agent.attributedNet)}
                <span className="text-muted-foreground block text-xs">
                  {t('support.agents.orders', { count: agent.attributedOrders })}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      ) : null}
    </TableCard>
  );
}

function TagsTable({
  report,
  isLoading,
}: {
  report: SupportReportData | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const tags = report?.tags ?? [];
  return (
    <TableCard
      title={t('support.tags.title')}
      columns={2}
      isLoading={isLoading}
      empty={!isLoading && tags.length === 0 ? t('support.tags.empty') : null}
    >
      <TableHeader>
        <TableRow>
          <TableHead className="pl-5">{t('support.columns.tag')}</TableHead>
          <TableHead className="pr-5 text-right">{t('support.columns.conversations')}</TableHead>
        </TableRow>
      </TableHeader>
      {!isLoading && tags.length > 0 ? (
        <TableBody>
          {tags.map((tag) => (
            <TableRow key={tag.tag}>
              <TableCell className="pl-5">{tag.tag}</TableCell>
              <TableCell className="pr-5 text-right tabular-nums">
                {format.number(tag.count)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      ) : null}
    </TableCard>
  );
}

function ServiceCard({ report }: { report: SupportReportData | undefined }) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const duration = useDurationFormat();
  const totals = report?.totals;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[15px]">{t('support.service.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {report && totals ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <dt className="text-muted-foreground">{t('support.service.firstResponseAvg')}</dt>
            <dd className="text-right tabular-nums">{duration(totals.firstResponseSecondsAvg)}</dd>
            <dt className="text-muted-foreground">{t('support.service.resolutionAvg')}</dt>
            <dd className="text-right tabular-nums">{duration(totals.resolutionSecondsAvg)}</dd>
            <dt className="text-muted-foreground">{t('support.service.offline')}</dt>
            <dd className="text-right tabular-nums">{format.number(totals.offlineMessages)}</dd>
            <dt className="text-muted-foreground">{t('support.service.ticketsClosed')}</dt>
            <dd className="text-right tabular-nums">{format.number(totals.ticketsClosed)}</dd>
            <dt className="text-muted-foreground">{t('support.service.ratings')}</dt>
            <dd className="text-right tabular-nums">
              {t('support.agents.ratings', {
                positive: format.number(totals.ratingsPositive),
                neutral: format.number(totals.ratingsNeutral),
                negative: format.number(totals.ratingsNegative),
              })}
            </dd>
            <dt className="text-muted-foreground">{t('support.service.firstResponseBreaches')}</dt>
            <dd className="text-right tabular-nums">
              {format.number(report.sla.firstResponseBreaches)}
            </dd>
            <dt className="text-muted-foreground">{t('support.service.resolutionBreaches')}</dt>
            <dd className="text-right tabular-nums">
              {format.number(report.sla.resolutionBreaches)}
            </dd>
          </dl>
        ) : (
          <Skeleton className="h-40" />
        )}
      </CardContent>
    </Card>
  );
}

function SupportReport() {
  const { t } = useTranslation('reports');
  const store = useStore();
  const days = useDayFormat();
  const { range, setRange } = useReportRange();
  const query = useSupportReport(store.id, range);
  const report = query.data;
  const previous = previousRange(range);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 lg:flex-row lg:items-start lg:justify-between">
        <RangePicker range={range} onChange={setRange} />
        <div className="flex flex-wrap items-start gap-2">
          <RollupBar storeId={store.id} range={range} lastRollupAt={report?.lastRollupAt} />
          <SupportExportMenu report={report} />
        </div>
      </div>
      <p className="text-muted-foreground -mt-3 text-xs">
        {t('overview.comparedWith', { range: days.range(previous.from, previous.to) })}
      </p>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <SupportKpis
            totals={report?.totals}
            previous={report?.previous}
            isLoading={query.isPending}
          />
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-[15px]">{t('support.daily')}</CardTitle>
              </CardHeader>
              <CardContent>
                {report ? (
                  <SupportChart
                    summary={t('support.chartSummary', {
                      range: days.range(report.from, report.to),
                      chats: report.totals.chatsStarted,
                      missed: report.totals.chatsMissed,
                      tickets: report.totals.ticketsOpened,
                    })}
                    points={report.series.map((day) => ({
                      label: days.short(day.date),
                      answered: day.chatsStarted - day.chatsMissed,
                      missed: day.chatsMissed,
                      tickets: day.ticketsOpened,
                    }))}
                  />
                ) : (
                  <Skeleton className="h-[280px] w-full" />
                )}
              </CardContent>
            </Card>
            <AttributionCard totals={report?.totals} />
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-[15px]">{t('support.missed.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                {report ? (
                  <MissedHeatmap cells={report.missedByHour} timezone={report.timezone} />
                ) : (
                  <Skeleton className="h-40 w-full" />
                )}
              </CardContent>
            </Card>
            <ServiceCard report={report} />
          </div>
          <div className="grid gap-4 xl:grid-cols-3">
            <div className="xl:col-span-2">
              <AgentsTable report={report} isLoading={query.isPending} />
            </div>
            <TagsTable report={report} isLoading={query.isPending} />
          </div>
        </>
      )}
    </div>
  );
}

export function SupportReportPage() {
  return (
    <FeatureErrorBoundary name="reports.support">
      <SupportReport />
    </FeatureErrorBoundary>
  );
}
